import React, { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Text, Billboard } from "@react-three/drei";
import { playerColor, teamOf, ARENA, FONTS } from "../game/theme";

// Bobblehead player. `mood`: idle | celebrate | sad. Changing `bump` makes it jump
// (used when the player fires an answer). Colors are seeded by player id.
const Avatar = ({
  player,
  position = [0, 0, 0],
  mood = "idle",
  bump = 0,
  scale = 1,
  highlight = false,
}) => {
  const group = useRef();
  const headRef = useRef();
  const ringRef = useRef();
  const lastBump = useRef(bump);
  const jumpV = useRef(0);
  const jumpY = useRef(0);

  const { bodyColor, seed, team } = useMemo(
    () => ({
      bodyColor: playerColor(player?.id || player?.name || "?"),
      seed: (player?.id || "x").split("").reduce((a, c) => a + c.charCodeAt(0), 0),
      team: teamOf(player?.team),
    }),
    [player]
  );

  useFrame(({ clock }, dt) => {
    if (!group.current) return;
    const t = clock.elapsedTime + seed;

    // Jump impulse when bump changes
    if (bump !== lastBump.current) {
      lastBump.current = bump;
      jumpV.current = 5.5;
    }
    if (jumpY.current > 0 || jumpV.current > 0) {
      jumpV.current -= 18 * dt;
      jumpY.current = Math.max(0, jumpY.current + jumpV.current * dt);
      if (jumpY.current === 0) jumpV.current = 0;
    }

    let bob = Math.sin(t * 2.2) * 0.06;
    let tilt = Math.sin(t * 1.7) * 0.05;
    let spin = 0;
    if (mood === "celebrate") {
      bob = Math.abs(Math.sin(t * 6)) * 0.45;
      spin = t * 2.5;
      tilt = 0;
    } else if (mood === "sad") {
      bob = -0.12;
      tilt = 0.28;
    }
    group.current.position.set(position[0], position[1] + bob + jumpY.current, position[2]);
    group.current.rotation.set(mood === "sad" ? 0.25 : 0, spin, tilt);

    if (headRef.current) {
      headRef.current.rotation.z = Math.sin(t * 3) * 0.08;
    }
    if (ringRef.current) {
      ringRef.current.material.emissiveIntensity = highlight
        ? 2.2 + Math.sin(clock.elapsedTime * 8) * 1
        : 0.9;
    }
  });

  return (
    <group ref={group} scale={scale}>
      {/* base ring in team color */}
      <mesh ref={ringRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <torusGeometry args={[0.55, 0.05, 10, 40]} />
        <meshStandardMaterial
          color={team.color}
          emissive={team.color}
          emissiveIntensity={0.9}
          toneMapped={false}
        />
      </mesh>
      {/* fake soft shadow */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
        <circleGeometry args={[0.5, 24]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.35} />
      </mesh>

      {/* body */}
      <mesh position={[0, 0.75, 0]}>
        <capsuleGeometry args={[0.34, 0.55, 8, 20]} />
        <meshStandardMaterial color={bodyColor} roughness={0.35} metalness={0.15} />
      </mesh>
      {/* belly badge in team color */}
      <mesh position={[0, 0.78, 0.3]}>
        <sphereGeometry args={[0.14, 16, 16]} />
        <meshStandardMaterial
          color={team.color}
          emissive={team.color}
          emissiveIntensity={0.7}
          toneMapped={false}
        />
      </mesh>

      {/* head — oversized for cuteness */}
      <group ref={headRef} position={[0, 1.5, 0]}>
        <mesh>
          <sphereGeometry args={[0.38, 24, 24]} />
          <meshStandardMaterial color={bodyColor} roughness={0.3} />
        </mesh>
        {/* eyes */}
        <mesh position={[-0.13, 0.06, 0.32]}>
          <sphereGeometry args={[0.085, 12, 12]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
        <mesh position={[0.13, 0.06, 0.32]}>
          <sphereGeometry args={[0.085, 12, 12]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
        <mesh position={[-0.115, 0.06, 0.39]}>
          <sphereGeometry args={[0.04, 10, 10]} />
          <meshStandardMaterial color="#111111" />
        </mesh>
        <mesh position={[0.145, 0.06, 0.39]}>
          <sphereGeometry args={[0.04, 10, 10]} />
          <meshStandardMaterial color="#111111" />
        </mesh>
        {/* admin crown */}
        {player?.is_admin && (
          <mesh position={[0, 0.48, 0]} rotation={[0, Math.PI / 8, 0]}>
            <cylinderGeometry args={[0.22, 0.16, 0.18, 5]} />
            <meshStandardMaterial
              color={ARENA.gold}
              emissive={ARENA.gold}
              emissiveIntensity={0.5}
              metalness={0.8}
              roughness={0.25}
            />
          </mesh>
        )}
      </group>

      {/* name tag (sized up — avatars render at ~0.7 scale) */}
      <Billboard position={[0, 2.45, 0]}>
        <Text
          font={FONTS.tech}
          fontSize={0.3}
          color={highlight ? ARENA.gold : "#ffffff"}
          outlineWidth={0.014}
          outlineColor="#000000"
          anchorX="center"
          anchorY="middle"
          maxWidth={3}
        >
          {player?.name || "?"}
        </Text>
      </Billboard>
    </group>
  );
};

export default Avatar;
